/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'
import { flexRender } from '@tanstack/react-table'
import { BarsArrowDownIcon, BarsArrowUpIcon } from '@heroicons/react/24/solid'
import UiTableColumnSearchToggle from '../UiTableColumnSearchToggle'
import { TableInitBase } from 'utils/hooks/useTable'

interface TableHeadColumnTitleProps {
    className?: string
    children?: React.ReactNode | React.ReactNode[]
    header: any
    tableInit: TableInitBase
    personsCount?: number
    showSearchToggle?: boolean
    isSearchVisible?: boolean
    onToggleSearch?: () => void
}

const TableHeadColumnTitle = (props: TableHeadColumnTitleProps) => {
    const headerId = props.header.column.id
    const isIconHeader = headerId === 'actions' || headerId === 'select'
    const headerContent = flexRender(props.header.column.columnDef.header, props.header.getContext())

    return (
        <div
            className={clsx(
                'TableHeadColumnTitle flex flex-row items-center gap-0.5 min-h-6 flex-nowrap min-w-0',
                props.className && props.className
            )}
        >
            {isIconHeader ? headerContent : <span className="min-w-0 flex-1">{headerContent}</span>}

            {props.showSearchToggle && props.onToggleSearch && (
                <UiTableColumnSearchToggle
                    onClick={props.onToggleSearch}
                    isActive={props.isSearchVisible || !!props.header.column.getFilterValue()}
                />
            )}

            {props.header.column.columnDef.enableSorting && (
                <button
                    disabled={props.tableInit.isLoading}
                    type={'button'}
                    title={'Sort'}
                    className={clsx('p-0.5 rounded transition-colors', 'hover:bg-gray-100 dark:hover:bg-gray-800')}
                    onClick={() => props.tableInit.sortChangeHandler(props.header.column.id)}
                >
                    {{
                        asc: <BarsArrowDownIcon className={clsx('h-4 w-4 text-blue-500')} />,
                        desc: <BarsArrowUpIcon className={clsx('h-4 w-4 text-blue-500')} />,
                    }[props.header.column.getIsSorted() as string] ?? null}
                    {!props.header.column.getIsSorted() && (
                        <BarsArrowUpIcon className={clsx('h-4 w-4 text-gray-500 hover:text-gray-700')} />
                    )}
                </button>
            )}
            {props.children && props.children}
        </div>
    )
}

export default TableHeadColumnTitle
